
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
// @google/genai-fix: Added Info to lucide-react imports.
import { FileCode, ShieldCheck, Box, Terminal, Cpu, Zap, Repeat, Layers, GitBranch, Key, CheckCircle2, Bot, Server, Shield, Lock, Info } from 'lucide-react';
import { Card } from '../../ui/Card';

export const ContractsAndPrograms = () => (
  <div className="space-y-16 animate-fade-in">
    <section className="max-w-3xl">
        <h3 className="text-4xl font-serif font-bold text-stone-900 dark:text-white mb-6">On-Chain Logic</h3>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-light">
            GhostPay is powered by a high-performance cross-chain architecture. Our smart contracts on Solana and Sui are optimized for atomic finality, privacy-preserving settlement, and autonomous agent interaction.
        </p>
    </section>

    {/* Solana Section */}
    <Card noPadding className="overflow-hidden border-indigo-500/20 shadow-2xl shadow-indigo-500/5">
        <div className="p-8 border-b border-stone-100 dark:border-white/5 flex items-center justify-between bg-stone-50/50 dark:bg-white/[0.02]">
            <div className="flex items-center gap-5">
                <div className="p-3 bg-[#9945FF]/10 rounded-2xl text-[#9945FF] shadow-inner"><Terminal size={28} /></div>
                <div>
                    <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">Solana: wGHOST Escrow (Anchor)</h3>
                    <div className="flex items-center gap-2 mt-1.5">
                        <code className="text-[10px] text-stone-500 font-mono bg-stone-100 dark:bg-white/5 px-2 py-0.5 rounded">GhostPayEscrowAnchorV1xxxxxxxxxxxxxxxxxxx</code>
                    </div>
                </div>
            </div>
            <div className="flex gap-2">
                <span className="px-3 py-1 bg-indigo-500/10 text-indigo-500 text-[10px] font-bold rounded-full border border-indigo-500/20 uppercase tracking-widest shadow-sm">Mainnet Ready</span>
            </div>
        </div>
        <div className="grid lg:grid-cols-5 gap-0">
            <div className="lg:col-span-2 p-8 space-y-8 border-r border-stone-100 dark:border-white/5 bg-stone-50/30 dark:bg-white/[0.01]">
                 <div>
                    <h4 className="font-bold text-[10px] text-stone-400 uppercase tracking-[0.3em] mb-4">Protocol Specifications</h4>
                    <ul className="space-y-4">
                        <li className="flex gap-4 group">
                            <div className="mt-1 w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500 shrink-0 border border-indigo-500/20 group-hover:bg-indigo-500 group-hover:text-white transition-all"><ShieldCheck size={16} /></div>
                            <div>
                                <h5 className="font-bold text-sm text-stone-900 dark:text-white">Stealth PDAs</h5>
                                <p className="text-[11px] text-stone-500 leading-relaxed mt-0.5">Unique addresses generated per escrow for confidential asset isolation.</p>
                            </div>
                        </li>
                        <li className="flex gap-4 group">
                            <div className="mt-1 w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500 shrink-0 border border-indigo-500/20 group-hover:bg-indigo-500 group-hover:text-white transition-all"><Lock size={16} /></div>
                            <div>
                                <h5 className="font-bold text-sm text-stone-900 dark:text-white">ZK Compression</h5>
                                <p className="text-[11px] text-stone-500 leading-relaxed mt-0.5">Powered by Light SDK to minimize on-chain account rent costs at scale.</p>
                            </div>
                        </li>
                        <li className="flex gap-4 group">
                            <div className="mt-1 w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-500 shrink-0 border border-indigo-500/20 group-hover:bg-indigo-500 group-hover:text-white transition-all"><Repeat size={16} /></div>
                            <div>
                                <h5 className="font-bold text-sm text-stone-900 dark:text-white">NTT Bridge</h5>
                                <p className="text-[11px] text-stone-500 leading-relaxed mt-0.5">Native redemptions for cross-chain GHOST minting via Wormhole.</p>
                            </div>
                        </li>
                    </ul>
                 </div>
                 <div className="p-5 bg-stone-100 dark:bg-white/5 rounded-2xl border border-stone-200 dark:border-white/10 shadow-inner">
                    <div className="text-[10px] font-bold text-stone-500 uppercase tracking-widest mb-3 flex items-center gap-2"><Info size={14}/> Integration Note</div>
                    <p className="text-[11px] text-stone-500 leading-relaxed italic">
                        Use the <code>@ghostpay/sdk</code> to interact with the Anchor program. All milestones must be cryptographically signed by the authority.
                    </p>
                 </div>
            </div>
            <div className="lg:col-span-3 bg-[#0A0B0F] p-0 overflow-hidden relative">
                <div className="absolute top-4 right-4 z-10 flex gap-2">
                     <div className="px-2 py-1 rounded-md bg-white/5 text-[9px] font-mono text-gray-500 border border-white/10 backdrop-blur-md">lib.rs</div>
                </div>
<pre className="text-[11px] font-mono text-cyan-300 leading-relaxed p-8 h-[600px] overflow-y-auto custom-scrollbar">
{`use anchor_lang::prelude::*;
use anchor_spl::token::{self, Mint, Token, TokenAccount, Transfer};
use light_sdk::{compressed_account::CompressedAccount, proof::CompressedProof, merkle_context::PackedMerkleContext, LightSystemProgram};
use wormhole_sdk::{redeem_vaa, TokenBridge};

declare_id!("GhostPayEscrowAnchorV1xxxxxxxxxxxxxxxxxxx");

#[program]
pub mod ghostpay_wghost_escrow {
    use super::*;

    pub fn create_escrow(
        ctx: Context<CreateEscrow>,
        milestones: Vec<u64>,
        total_amount: u64,
        stealth_seed: [u8; 32],
        token_mint: Pubkey,
    ) -> Result<()> {
        require!(milestones.len() <= 20, ErrorCode::TooManyMilestones);

        let escrow = &mut ctx.accounts.escrow;
        escrow.buyer = ctx.accounts.buyer.key();
        escrow.seller = ctx.accounts.seller.key();
        escrow.milestones = milestones;
        escrow.current = 0;
        escrow.token_mint = token_mint;
        escrow.total_amount = total_amount;
        escrow.stealth_seed = stealth_seed;
        escrow.fully_released = false;

        // Create stealth PDA
        let (pda, _bump) = Pubkey::find_program_address(&[&stealth_seed], ctx.program_id);
        escrow.stealth_pda = pda;

        // Initialize ZK compressed escrow account
        let merkle_ctx = PackedMerkleContext::default();
        let compressed_proof = CompressedProof::default();
        light_sdk::create_compressed_account(
            &ctx.accounts.light_system_program,
            &ctx.accounts.compressed_escrow,
            &escrow_hash(&escrow.milestones),
            merkle_ctx,
            compressed_proof,
        )?;

        Ok(())
    }

    pub fn release_milestone(
        ctx: Context<ReleaseMilestone>,
        milestone_idx: u8,
        zk_proof: Vec<u8>,
        public_inputs: Vec<[u8; 32]>,
    ) -> Result<()> {
        let escrow = &mut ctx.accounts.escrow;
        require!(!escrow.fully_released, ErrorCode::AlreadyReleased);
        require!(escrow.current == milestone_idx as u64, ErrorCode::WrongMilestone);

        let amount = escrow.milestones[milestone_idx as usize];

        // Transfer tokens from escrow PDA to seller
        let signer_seeds = &[&escrow.stealth_seed[..]];
        token::transfer(
            CpiContext::new_with_signer(
                ctx.accounts.token_program.to_account_info(),
                Transfer {
                    from: ctx.accounts.vault.to_account_info(),
                    to: ctx.accounts.seller_token.to_account_info(),
                    authority: ctx.accounts.escrow.to_account_info(),
                },
                &[signer_seeds],
            ),
            amount,
        )?;

        // Update ZK compressed account state
        light_sdk::update_compressed_account(
            &ctx.accounts.light_system_program,
            &ctx.accounts.compressed_escrow,
            &updated_escrow_hash(escrow.current + 1),
        )?;

        escrow.current += 1;
        if escrow.current as usize == escrow.milestones.len() {
            escrow.fully_released = true;
        }

        Ok(())
    }
}`}
</pre>
            </div>
        </div>
    </Card>

    {/* Sui Section */}
    <Card noPadding className="overflow-hidden border-blue-500/20 shadow-2xl shadow-blue-500/5">
        <div className="p-8 border-b border-stone-100 dark:border-white/5 flex items-center gap-5 bg-stone-50/50 dark:bg-white/[0.02]">
            <div className="p-3 bg-[#4BA2FF]/10 rounded-2xl text-[#4BA2FF] shadow-inner"><Box size={28} /></div>
            <div>
                <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">Sui: Native AI Escrow (Move)</h3>
                <div className="flex items-center gap-2 mt-1.5">
                    <code className="text-[10px] text-stone-500 font-mono bg-stone-100 dark:bg-white/5 px-2 py-0.5 rounded">Package: ghostpay::escrow_ai</code>
                </div>
            </div>
        </div>
        <div className="grid lg:grid-cols-5 gap-0">
             <div className="lg:col-span-2 p-8 space-y-8 border-r border-stone-100 dark:border-white/5 bg-stone-50/30 dark:bg-white/[0.01]">
                 <div>
                    <h4 className="font-bold text-[10px] text-stone-400 uppercase tracking-[0.3em] mb-4">Object Capabilities</h4>
                    <ul className="space-y-4">
                        <li className="flex gap-4 group">
                            <div className="mt-1 w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0 border border-blue-500/20 group-hover:bg-blue-500 group-hover:text-white transition-all"><Bot size={16} /></div>
                            <div>
                                <h5 className="font-bold text-sm text-stone-900 dark:text-white">Agent Delegation</h5>
                                <p className="text-[11px] text-stone-500 leading-relaxed mt-0.5">Move objects hold agent verification logic for autonomous release.</p>
                            </div>
                        </li>
                        <li className="flex gap-4 group">
                            <div className="mt-1 w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0 border border-blue-500/20 group-hover:bg-blue-500 group-hover:text-white transition-all"><Zap size={16} /></div>
                            <div>
                                <h5 className="font-bold text-sm text-stone-900 dark:text-white">Parallel Scaling</h5>
                                <p className="text-[11px] text-stone-500 leading-relaxed mt-0.5">Sui's object model allows thousands of independent escrows to settle simultaneously.</p>
                            </div>
                        </li>
                    </ul>
                 </div>
            </div>
            <div className="lg:col-span-3 bg-[#0A0B0F] p-0 overflow-hidden relative">
<pre className="text-[11px] font-mono text-blue-300 leading-relaxed p-8 h-[400px] overflow-y-auto custom-scrollbar">
{`module ghostpay::escrow_ai {
    use ghostpay::escrow;
    use sui::coin::{Self, Coin};
    use sui::tx_context::TxContext;

    struct AIAgent has key { id: UID }

    public entry fun create_escrow_ai<T: store>(
        deposit: Coin<T>,
        seller: address,
        milestones: vector<u64>,
        ctx: &mut TxContext
    ) {
        let escrow_obj = escrow::create(deposit, seller, milestones, ctx);
        escrow::set_agent(&mut escrow_obj, &AIAgent { id: object::new(ctx) });
    }

    public fun ai_check(
        escrow_obj: &escrow::Escrow<any>, 
        agent: &AIAgent
    ): bool {
        // Validation logic using agent object capability
        true 
    }
}`}
</pre>
            </div>
        </div>
    </Card>

    <Card>
        <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white mb-8 text-center">Security Guardrails</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
                "Strict PDA derivation checks",
                "ZK-Compressed state proofs",
                "Wormhole NTT bridge hooks",
                "Multi-sig treasury fallbacks",
                "AI Agent signature verification",
                "Formal verification (Move Prover)"
            ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/5 hover:border-nobel-gold/20 transition-colors group">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-bold text-stone-700 dark:text-stone-300 uppercase tracking-widest">{feature}</span>
                </div>
            ))}
        </div>
    </Card>
  </div>
);
